import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useDigest, useDigestActions, type DigestRun } from "@/lib/admin";

/**
 * Admin → Digest (superadmin): the daily digest's settings, its last two weeks
 * of runs, a Preview that sends nothing and a Send now.
 *
 * ⚠ The tab is only offered to a superadmin, but that is presentation: every
 * route behind it is `requireSuperadmin` on the server.
 */

const VERDICT: Record<
  NonNullable<DigestRun["verdict"]>,
  { label: string; variant: "success" | "warning" | "destructive" }
> = {
  ok: { label: "✓ All good", variant: "success" },
  warn: { label: "⚠ To check", variant: "warning" },
  fail: { label: "✗ Failing", variant: "destructive" },
};

function sgt(iso: string): string {
  return `${new Date(iso).toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })} SGT`;
}

function RunStatus({ run }: { run: DigestRun }) {
  if (run.status === "sent") {
    return (
      <span>
        ✓ {run.finishedAt ? sgt(run.finishedAt) : "sent"}
        {run.attempts > 1 ? ` (attempt ${run.attempts})` : ""}
      </span>
    );
  }
  if (run.status === "running") return <span>Sending… (attempt {run.attempts})</span>;
  return (
    <span className="text-destructive">
      ✗ Failed after {run.attempts} attempt{run.attempts === 1 ? "" : "s"}: {run.error}
    </span>
  );
}

export function DigestAdmin() {
  const digest = useDigest();
  const { preview, send } = useDigestActions();

  if (digest.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (digest.isError)
    return <p className="text-destructive text-sm">Could not load the digest.</p>;
  const { settings, runs } = digest.data;

  return (
    <div className="space-y-6">
      <section className="border-border bg-card space-y-2 rounded-lg border p-4 text-sm">
        <p>
          The daily digest goes to{" "}
          <span className="font-medium">
            {settings.recipients.join(", ") || "nobody"}
          </span>
          . {settings.schedule}, up to {settings.maxAttempts} attempts a day. It reports
          whether Home and the apps behind the cards are healthy, who signed in, which
          apps they launched, and every admin action. It is sent on quiet days too, so a
          morning without one means Home or its email is down.
        </p>
        {settings.problems.length > 0 && (
          <p className="text-destructive">Not sending: {settings.problems.join("; ")}</p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            variant="secondary"
            size="sm"
            disabled={preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? "Building…" : "Preview"}
          </Button>
          <Button
            size="sm"
            disabled={send.isPending || !settings.enabled}
            onClick={() => {
              if (
                window.confirm(
                  `Send the digest for the last 24 hours to ${settings.recipients.join(", ")} now? The 07:00 digest still goes.`,
                )
              ) {
                send.mutate();
              }
            }}
          >
            {send.isPending ? "Sending…" : "Send now"}
          </Button>
        </div>
        {send.isSuccess && (
          <p className="text-muted-foreground text-xs">Sent: {send.data.subject}</p>
        )}
        {send.isError && (
          <p className="text-destructive text-xs">
            {send.error instanceof Error ? send.error.message : "Not sent."}
          </p>
        )}
        {preview.isError && (
          <p className="text-destructive text-xs">Could not build the preview.</p>
        )}
        <p className="text-muted-foreground text-xs">
          Preview sends nothing. Send now is recorded in the audit log and does not use up
          the morning's digest. Press it after a deploy, so a wrong address shows up
          today.
        </p>
      </section>

      {preview.data && (
        <section>
          <h2 className="mb-2 text-sm font-semibold break-words">
            Preview: {preview.data.subject}
          </h2>
          {/* Sandboxed: the preview is our own escaped HTML, and it still gets no
              scripts and no same-origin access. White, because it is an email. */}
          <iframe
            title="Digest preview"
            sandbox=""
            srcDoc={preview.data.html}
            className="border-border h-[640px] w-full rounded-lg border bg-white"
          />
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold">Last two weeks</h2>
        <div className="border-border bg-card overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[34rem] text-left text-sm">
            <thead className="text-muted-foreground text-xs uppercase">
              <tr>
                <th className="px-4 py-2.5 font-medium">Day</th>
                <th className="py-2.5 pr-3 font-medium">Sent</th>
                <th className="py-2.5 pr-3 font-medium">Health</th>
                <th className="py-2.5 pr-3 text-right font-medium">People</th>
                <th className="py-2.5 pr-3 text-right font-medium">Launches</th>
                <th className="py-2.5 pr-4 text-right font-medium">Admin actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr className="border-border border-t">
                  <td
                    colSpan={6}
                    className="text-muted-foreground px-4 py-4 text-center text-xs"
                  >
                    No digest has run yet. The first goes at 07:00 SGT.
                  </td>
                </tr>
              )}
              {runs.map((r) => (
                <tr key={r.day} className="border-border border-t align-top tabular-nums">
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.day}</td>
                  <td className="py-2.5 pr-3 text-xs">
                    <RunStatus run={r} />
                  </td>
                  <td className="py-2.5 pr-3">
                    {r.verdict ? (
                      <Badge
                        variant={VERDICT[r.verdict].variant}
                        className="whitespace-nowrap"
                      >
                        {VERDICT[r.verdict].label}
                        {r.headline && r.verdict !== "ok"
                          ? ` (${r.headline.checks.fail + r.headline.checks.warn})`
                          : ""}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right">{r.headline?.people ?? "—"}</td>
                  <td className="py-2.5 pr-3 text-right">
                    {r.headline?.launches ?? "—"}
                  </td>
                  <td className="py-2.5 pr-4 text-right">
                    {r.headline?.adminActions ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
